// The cloud windows in binary parts (server: simulation/src/cloudvol_wire.py, which has the format).
//
// A "cloud_win" message is a begin frame (the message without its density, light and roughness, plus a manifest of the parts) and then one binary frame per part (a slab of
// one of those three arrays, deflated, with each layer stored as its difference from the layer below). This module tells those frames from the physics frames, inflates the
// parts as they arrive (the browser's decompressor works off the main thread, so the early parts are done while the later ones are still coming), undoes the layer filter,
// and hands the whole message to `CloudVolume.ingest` with the arrays in `dec`.
//
// The windows are separate streams: the low three bits of the package id are the ring level (0 the finest, 4 the world), and each stream keeps its newest package only. A begin
// frame of one ring drops that ring's unfinished package and nobody else's, so a slow coarse window is not cut short by a fast fine one.

import type { CloudWinMessage } from './cloudVolume';

/** Bytes common to the header of both frame types: "CVP", version, type, 0, 0, 0xFF, then the u32 package id. */
const HEAD = 12;
const KIND_BEGIN = 1, KIND_PART = 2;
const STREAMS = 8;

/** True for one of this module's frames. A physics frame starts with its tick as a u64; the header's last byte is 0xFF, which no tick below 2^56 has there. */
export function isVolumeFrame(buf: ArrayBuffer): boolean {
  if (buf.byteLength < HEAD) return false;
  const b = new Uint8Array(buf, 0, 8);
  return b[0] === 0x43 && b[1] === 0x56 && b[2] === 0x50 && b[3] === 1 && b[7] === 0xFF;
}

async function inflateZ(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Undo the layer filter in place: each layer (`stride` bytes) was stored as its difference from the layer below it, modulo 256; the lowest layer as it is. A running sum. */
export function unfilter(a: Uint8Array, stride: number): void {
  for (let i = stride; i < a.length; i++) a[i] = (a[i]! + a[i - stride]!) & 255;
}

type Kind = 'vol' | 'lit' | 'win';
interface PartSpec { k: Kind; o: number; n: number; st: number }
interface Manifest { len: Record<Kind, number>; parts: PartSpec[] }
type Meta = Omit<CloudWinMessage, 'dec'> & { bin: Manifest };
interface Assembly { id: number; meta: Meta; arrays: Record<Kind, Uint8Array>; left: number }

/** Collects the frames of the newest package of each stream. A begin frame starts a package and drops an unfinished one of the same stream; a part of any package but its stream's
 *  current one is ignored. When the last part of a package is in, `onMessage` gets the message: the begin frame's JSON with `dec` {vol, lit, win} (the arrays whole, unfiltered;
 *  `win` is the roughness lattice). */
export class VolumeAssembler {
  private cur: (Assembly | null)[] = new Array(STREAMS).fill(null);
  /** packages abandoned unfinished (a newer one of the same ring began first), for the console */
  dropped = 0;

  constructor(private readonly onMessage: (m: CloudWinMessage) => void, private readonly onError?: (e: unknown) => void) {}

  accept(buf: ArrayBuffer): void {
    const v = new DataView(buf);
    const kind = v.getUint8(4), id = v.getUint32(8, true), s = id & (STREAMS - 1);
    if (kind === KIND_BEGIN) {
      let meta: Meta;
      try {
        meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 16, v.getUint32(12, true))));
        if (!meta.bin || !Array.isArray(meta.bin.parts)) throw new Error('no manifest');
      } catch (e) { this.cur[s] = null; this.onError?.(e); return; }
      const old = this.cur[s];
      if (old && old.left > 0) this.dropped++;
      const L = meta.bin.len;
      this.cur[s] = { id, meta, arrays: { vol: new Uint8Array(L.vol), lit: new Uint8Array(L.lit), win: new Uint8Array(L.win) }, left: meta.bin.parts.length };
      return;
    }
    const a = this.cur[s];
    if (kind !== KIND_PART || !a || a.id !== id) return;
    const spec = a.meta.bin.parts[v.getUint16(12, true)];
    if (!spec) return;
    inflateZ(new Uint8Array(buf, 14)).then(raw => {
      if (this.cur[s] !== a) return;                                       // a newer package of this ring began while this one was inflating
      if (raw.length !== spec.n || spec.o + spec.n > a.arrays[spec.k].length) throw new Error(`cloud window part ${spec.k}@${spec.o}: ${raw.length} bytes, expected ${spec.n}`);
      if (spec.st) unfilter(raw, spec.st);
      a.arrays[spec.k].set(raw, spec.o);
      if (--a.left === 0) {
        this.cur[s] = null;
        const { bin: _bin, ...rest } = a.meta;
        void _bin;
        this.onMessage({ ...rest, dec: a.arrays });
      }
    }).catch(e => { if (this.cur[s] === a) this.cur[s] = null; this.onError?.(e); });
  }
}
